import { useForm } from '@inertiajs/react'
import AuthLayout from '@/layouts/AuthLayout'
import type { FormEvent } from 'react'

interface SignupProps {
  email_sent?: boolean
}

export default function Signup({ email_sent }: SignupProps) {
  const { data, setData, post, processing, errors } = useForm({
    account_name: '',
    email_address: '',
    password: '',
    password_confirmation: '',
    country: '',
    organization_type: '',
    terms_accepted: false,
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/signup')
  }

  if (email_sent) {
    return (
      <AuthLayout title="Check your email">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body items-center text-center">
            <span className="iconify lucide--mail-check size-12 text-success mb-2" />
            <h2 className="card-title text-xl font-bold">Check your email</h2>
            <p className="text-base-content/70">
              We've sent a confirmation link to your email address. Please click
              the link to verify your account.
            </p>
            <div className="mt-4">
              <a href="/login" className="link link-primary text-sm">
                Back to login
              </a>
            </div>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Create your account">
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h2 className="card-title text-2xl font-bold mb-4">Create your account</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            {Object.keys(errors).length > 0 && (
              <div role="alert" className="alert alert-soft alert-error text-sm">
                <ul className="list-disc list-inside">
                  {Object.values(errors).map((error, i) => (
                    <li key={i}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Organization name</legend>
              <input
                type="text"
                className="input w-full"
                value={data.account_name}
                onChange={(e) => setData('account_name', e.target.value)}
                required
                autoFocus
                placeholder="e.g. Apex Leadership Consulting"
              />
              <p className="label text-xs whitespace-normal">Your consultancy or company name — it brands your surveys and reports.</p>
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Email</legend>
              <input
                type="email"
                className="input w-full"
                value={data.email_address}
                onChange={(e) => setData('email_address', e.target.value)}
                required
                autoComplete="email"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Password</legend>
              <input
                type="password"
                className="input w-full"
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Confirm password</legend>
              <input
                type="password"
                className="input w-full"
                value={data.password_confirmation}
                onChange={(e) => setData('password_confirmation', e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Country</legend>
              <select
                className="select w-full"
                value={data.country}
                onChange={(e) => setData('country', e.target.value)}
                required
              >
                <option value="">Select your country</option>
                <option value="US">United States</option>
                <option value="GB">United Kingdom</option>
                <option value="CA">Canada</option>
                <option value="AU">Australia</option>
                <option value="NZ">New Zealand</option>
                <option value="IE">Ireland</option>
                <option value="DE">Germany</option>
                <option value="NL">Netherlands</option>
                <option value="CH">Switzerland</option>
                <option value="SG">Singapore</option>
                <option value="AE">United Arab Emirates</option>
                <option value="ZA">South Africa</option>
                <option value="IN">India</option>
                <option disabled>──────────</option>
                <option value="AF">Afghanistan</option>
                <option value="AL">Albania</option>
                <option value="DZ">Algeria</option>
                <option value="AR">Argentina</option>
                <option value="AT">Austria</option>
                <option value="BE">Belgium</option>
                <option value="BR">Brazil</option>
                <option value="CL">Chile</option>
                <option value="CN">China</option>
                <option value="CO">Colombia</option>
                <option value="CZ">Czech Republic</option>
                <option value="DK">Denmark</option>
                <option value="EG">Egypt</option>
                <option value="FI">Finland</option>
                <option value="FR">France</option>
                <option value="GH">Ghana</option>
                <option value="GR">Greece</option>
                <option value="HK">Hong Kong</option>
                <option value="HU">Hungary</option>
                <option value="ID">Indonesia</option>
                <option value="IL">Israel</option>
                <option value="IT">Italy</option>
                <option value="JP">Japan</option>
                <option value="KE">Kenya</option>
                <option value="KR">South Korea</option>
                <option value="MY">Malaysia</option>
                <option value="MX">Mexico</option>
                <option value="NG">Nigeria</option>
                <option value="NO">Norway</option>
                <option value="PK">Pakistan</option>
                <option value="PH">Philippines</option>
                <option value="PL">Poland</option>
                <option value="PT">Portugal</option>
                <option value="RO">Romania</option>
                <option value="SA">Saudi Arabia</option>
                <option value="ES">Spain</option>
                <option value="SE">Sweden</option>
                <option value="TH">Thailand</option>
                <option value="TR">Turkey</option>
                <option value="UA">Ukraine</option>
                <option value="VN">Vietnam</option>
                <option value="OTHER">Other</option>
              </select>
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">How will you use ImpactCheck?</legend>
              <select
                className="select w-full"
                value={data.organization_type}
                onChange={(e) => setData('organization_type', e.target.value)}
                required
              >
                <option value="">Select an option</option>
                <option value="consultancy">I run programs for my clients</option>
                <option value="internal">I run programs for my organization</option>
              </select>
            </fieldset>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox checkbox-sm checkbox-primary mt-0.5"
                checked={data.terms_accepted}
                onChange={(e) => setData('terms_accepted', e.target.checked)}
                required
              />
              <span className="text-sm text-base-content/70">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="link link-primary">Terms of Service</a>
                {' '}and{' '}
                <a href="https://www.envisialearning.com/privacy_policy" target="_blank" rel="noopener" className="link link-primary">Privacy Policy</a>
              </span>
            </label>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={processing || !data.terms_accepted}
            >
              {processing && <span className="loading loading-spinner loading-sm" />}
              Sign up
            </button>
          </form>

          <p className="text-sm text-center mt-4">
            Already have an account?{' '}
            <a href="/login" className="link link-primary">
              Log in
            </a>
          </p>
        </div>
      </div>
    </AuthLayout>
  )
}
